import {useInfiniteQuery, UseInfiniteQueryResult} from '@tanstack/react-query';
import {z as r} from 'zod';

import {LogActionsEnum, LogTypesEnum} from '../../page/main/reviews/review-logs/review-log-type';
import {fetchAndDeserialize} from '../../util/api-adapter';

import {reviewResponsibleUserSchema} from './reviews-responsible-users';

const reviewLogUserSchema = r.object({
    id: r.number(),
    email: r.string(),
    firstName: r.string().nullable(),
    lastName: r.string().nullable(),
    fullName: r.string().nullable(),
});

type ReviewLogUserType = r.infer<typeof reviewLogUserSchema>;

const reviewTagLogDataSchema = r.object({
    loggedAt: r.string(),
    type: r.nativeEnum(LogTypesEnum),
    action: r.nativeEnum(LogActionsEnum),
    newValue: r.string().nullable(),
    oldValue: r.string().nullable(),
    user: reviewLogUserSchema.nullable(),
});

export type ReviewTagLogDataType = {
    action: LogActionsEnum;
    loggedAt: string;
    newValue: string | null;
    oldValue: string | null;
    type: LogTypesEnum.Tag;
    user: ReviewLogUserType | null;
};

const reviewGeneralLogDataSchema = r.object({
    loggedAt: r.string(),
    type: r.nativeEnum(LogTypesEnum),
    action: r.nativeEnum(LogActionsEnum),
    newValue: r.union([r.string(), reviewResponsibleUserSchema]),
    oldValue: r.union([r.string(), reviewResponsibleUserSchema]),
    title: r.string().nullable().optional(),
    user: reviewLogUserSchema.nullable(),
});

type ReviewResponsibleUserLogValueType = r.infer<typeof reviewResponsibleUserSchema>;

type ReviewGeneralLogDataType = {
    action: LogActionsEnum;
    loggedAt: string;
    newValue: string | ReviewResponsibleUserLogValueType;
    oldValue: string | ReviewResponsibleUserLogValueType;
    title?: string | null;
    type: Exclude<LogTypesEnum, LogTypesEnum.Tag>;
    user: ReviewLogUserType | null;
};

const reviewLogDataSchema = r.union([reviewTagLogDataSchema, reviewGeneralLogDataSchema]);

export type ReviewLogDataFromApiType = r.infer<typeof reviewLogDataSchema>;

export function isReviewTagLogData(log: ReviewLogDataFromApiType): log is ReviewTagLogDataType {
    return log.type === LogTypesEnum.Tag;
}

export function isReviewGeneralLogData(log: ReviewLogDataFromApiType): log is ReviewGeneralLogDataType {
    return log.type !== LogTypesEnum.Tag;
}

export function toReviewLogTimelineLog(log: ReviewGeneralLogDataType): ReviewLogTimelineLogType {
    return {...log, source: 'log'};
}

export type ReviewTagLogGroupType = {
    action: LogActionsEnum;
    loggedAt: string;
    source: 'log-tag-group';
    tagTitles: Array<string>;
    user: ReviewLogUserType | null;
};

type ReviewLogTimelineLogType = ReviewGeneralLogDataType & {
    source: 'log';
};

export type ReviewLogTimelineEntryType = ReviewLogTimelineLogType | ReviewTagLogGroupType;

const fetchReviewLogsSchema = r.object({
    results: r.array(reviewLogDataSchema),
    next: r.string().nullable(),
    previous: r.string().nullable(),
});

type FetchReviewsLogsType = r.infer<typeof fetchReviewLogsSchema>;

export function getReviewLogUserDisplayName(user: ReviewLogUserType): string {
    const {email, firstName, fullName, lastName} = user;

    return fullName || (firstName && lastName ? `${firstName} ${lastName}` : email);
}

function getReviewLogsListUrl(reviewId: number, nextPageCursor?: string | null) {
    const search = nextPageCursor ? '?cursor=' + new URL(nextPageCursor).searchParams.get('cursor') : '';

    return `/v4/reviews/${reviewId}/logs/${search}`;
}

async function fetchReviewLogsList(reviewId: number, nextPageCursor: string | null): Promise<FetchReviewsLogsType> {
    return fetchAndDeserialize<FetchReviewsLogsType>(
        getReviewLogsListUrl(reviewId, nextPageCursor),
        fetchReviewLogsSchema
    );
}

export function useReviewLogsList(reviewId: number, enabled?: boolean): UseInfiniteQueryResult<FetchReviewsLogsType> {
    return useInfiniteQuery(
        [getReviewLogsListUrl(reviewId)],
        ({pageParam: pageParameter = null}) => fetchReviewLogsList(reviewId, pageParameter),
        {
            getNextPageParam: (lastPage) => lastPage.next || null,
            enabled,
            refetchOnWindowFocus: false,
        }
    );
}
